/* src/components/suggestions/useSuggestionBadge.js */

// React binding for the Suggestions nav badge. The counting rules and the
// localStorage "seen" bookkeeping live in ./suggestionsBadge (pure, testable);
// this module only owns the Firestore subscription and the hook.
//
// Used by BOTH portals: AdminDashboard (isAdmin) and TutorDashboard.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  countNewSuggestions,
  countSuggestionNotifications,
  getSuggestionsSeenAt,
  markSuggestionsSeen,
} from "./suggestionsBadge";
import { subscribeSuggestions } from "./suggestionsLogic";

/**
 * Subscribes to the suggestions the viewer may read and derives the nav badge.
 * Returns `{ count, tone, newCount, markSeen }`; call `markSeen()` when the
 * Suggestions tab is opened. `enabled` lets a portal skip the extra listener.
 */
export default function useSuggestionBadge({ uid, isAdmin = false, enabled = true }) {
  const [rows, setRows] = useState([]);
  const [seenAtMs, setSeenAtMs] = useState(() => getSuggestionsSeenAt(uid));

  useEffect(() => {
    setSeenAtMs(getSuggestionsSeenAt(uid));
    if (!enabled || !uid) {
      setRows([]);
      return undefined;
    }
    return subscribeSuggestions({ uid, isAdmin }, setRows, () => {});
  }, [uid, isAdmin, enabled]);

  const badge = useMemo(
    () => countSuggestionNotifications(rows, { uid, seenAtMs }),
    [rows, uid, seenAtMs]
  );

  // Blue "new suggestions" count — the admin portal renders it next to the
  // red/grey badge; the tutor portal ignores it.
  const newCount = useMemo(() => countNewSuggestions(rows, { seenAtMs }), [rows, seenAtMs]);

  /** Mark everything currently visible as seen and drop the badge immediately. */
  const markSeen = useCallback(() => {
    markSuggestionsSeen(uid);
    setSeenAtMs(Date.now());
  }, [uid]);

  return { ...badge, newCount, markSeen };
}
